import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  Collapse,
} from "@material-ui/core";
import { makeStyles, createStyles, Theme, useTheme, ThemeProvider } from "@material-ui/core/styles";
import {
  PFXCriteria,
  PFXCriteriaForOrder,
  PFXCriteriaType,
  PFXOrderFulfillmentType,
  PFXTakeOut,
} from "../models/Interfaces";
import "moment/locale/es";
import moment from "moment-timezone";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { selectSocialFooter } from "../redux/slices/templateSlice";
import {
  IDeliveryCheckoutInfo,
  selectCartIsOpen,
  selectCleverlynk,
  selectCompany,
  selectDeliveryType,
  selectMandatoryCriteria,
  selectOptionalCriteria,
  selectZendesk,
} from "../redux/slices/cleverlynkSlice";
import useUserFormFunctions from "../hooks/useUserFormFunctions";
import Fallback from "../components/Tools/Fallback";
import { useCleverlynkContext } from "../contexts/CleverlynkContext";
import UserFormTextField from "../components/UserForm/UserFormTextField";
import UserFormPhoneField from "../components/UserForm/UserFormPhoneField";
import UserFormDateField from "../components/UserForm/UserFormDateField";
import UserFormSelectField from "../components/UserForm/UserFormSelectField";
import DeliveryInput from "../components/DeliveryMapService/DeliveryInput";
import useMapService from "../hooks/MapServiceHooks/useMapService";
import { selectDeliveryInfo, selectSelectedAddress } from "../redux/slices/mapServiceSlice";
import useDeliveryAddressCookies from "../hooks/MapServiceHooks/useDeliveryAddressCookies";
import SocialSpeedDials from "../components/Tools/SocialSpeedDial";
import AuthScreen from "../components/UserForm/AuthScreen/AuthScreen";
import { selectCustomerInfo } from "../redux/slices/userFormSlice";
import IdentificationField, { ALIMENTEC_LIST } from "../components/UserForm/IdentificationField";
import DeliveryTypeSlider from "../components/UserForm/DeliveryTypeSlider";
import { useClynkTheme } from "../hooks/useClynkTheme";
import useTemplateStyles from "../hooks/useTemplateStyles";

import { useTranslation } from "react-i18next";
import "../i18n/config";
import Header from "../components/Header/Header";
import ClynkFooter from "../components/Footer/ClynkFooter";

export const DATE_FORMAT = "DD/MM/YYYY";
export const DELIVERY = "Domicilio";
export const TAKEOUT = "TakeOut";

export const PREFERENCE_DTYPE = {
  Domicilio: "DELIVERY",
  TakeOut: "TAKEOUT",
};
export interface IUserInfo {
  identification: string;
  mandatoriesCriteria: string[][];
  optionalsCriteria: string[][];
  deliveryType: string;
  deliveryGeoInfo: IDeliveryCheckoutInfo | undefined;
}

export const onReplaceSpecialChars = (label: string) => {
  const chars = {
    á: "a",
    é: "e",
    í: "i",
    ó: "o",
    ú: "u",
    à: "a",
    è: "e",
    ì: "i",
    ò: "o",
    ù: "u",
    ñ: "n",
    Á: "A",
    É: "E",
    Í: "I",
    Ó: "O",
    Ú: "U",
    À: "A",
    È: "E",
    Ì: "I",
    Ò: "O",
    Ù: "U",
    Ñ: "N",
  };
  const expression = /[áàéèíìóòúùñ]/gi;
  const response = label.replace(expression, (e: string) => chars[e]);
  return response;
};

export interface IUserFormInput {
  label: string;
  value: any;
  mandatory: boolean;
  characterLimit?: number;
  onInputChange;
}

const UserForm = () => {
  const cleverlynkContext = useCleverlynkContext();

  const [openAuthDialog, setOpenAuthDialog] = useState<boolean>(true);
  const [calendarInput, setCalendarInput] = useState(new Map<string, moment.Moment>());

  const [clickedDeliverySlider, setClickedDeliverySlider] = useState<boolean>(false);

  const { t } = useTranslation(["datatreatment", "common"]);

  const {
    disableCustomerInfo,
    getDefaultValue,
    getValue,
    goToCheckout,
    handleModifyManyMandatoryCriteria,
    handleModifyDeliveryType,
    handleModifyManyOptionalCriteria,
    handleUploadCustomerData,
    resetCriteria,
    toggleCustomerInfoConsent,
    handleSetOpenedAuth,
    onInputChange,
  } = useUserFormFunctions();

  const { handleModifyDeliveryInfo } = useMapService();

  const { handleAddCookie } = useDeliveryAddressCookies();

  const optionalCriteria = useSelector(selectOptionalCriteria);
  const mandatoryCriteria = useSelector(selectMandatoryCriteria);
  const deliveryType = useSelector(selectDeliveryType);
  const deliveryInfo = useSelector(selectDeliveryInfo);
  const cleverlynk = useSelector(selectCleverlynk);
  const socialFooter = useSelector(selectSocialFooter);
  const cartIsOpen = useSelector(selectCartIsOpen);
  const selectedAddress = useSelector(selectSelectedAddress);
  const zendesk = useSelector(selectZendesk);
  const company = useSelector(selectCompany);

  const customerInfo = useSelector(selectCustomerInfo);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { createUserFormTheme } = useClynkTheme();
  const { titleStyles, primaryColor } = useTemplateStyles();

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      stickToBottom: {
        width: isMobile ? "90%" : "75%",
        position: "sticky",
        maxWidth: 700,
        margin: "auto",
        left: 0,
        zIndex: 1,
        bottom: 16,
        boxSizing: "border-box",
        MozBoxSizing: "border-box",
        WebkitBoxSizing: "border-box",
        paddingLeft: 0,
        paddingRight: 0,
      },
      nextbutton: {
        height: 50,
        borderRadius: 12,
        background:
          cleverlynk?.isUsingGeolocation && !selectedAddress && !(deliveryType === PFXOrderFulfillmentType.TAKEOUT)
            ? "#d4d4d4"
            : `linear-gradient(26deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
        color: theme.palette.primary.contrastText,
        fontWeight: "bold",
      },
      slideUpwardsGrid: {
        transitionProperty: "margin-top",
        transitionDuration: "500ms",
        marginTop: clickedDeliverySlider
          ? 0
          : (!!cleverlynk?.deliveryPrice || cleverlynk?.geolocationInfo) && cleverlynk?.takeOut
          ? 70
          : 15,
      },
      phoneInput: {
        marginTop: 0,
        marginBottom: 0,
      },
      customText: {
        lineHeight: 0.7,
      },
      customCheck: {
        padding: 2,
      },
      container: {
        minHeight: "inherit",
        maxWidth: 1000,
        paddingTop: 20,
        marginTop: theme.spacing(2),
      },
      content: {
        flexGrow: 1,
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        marginRight: 0,
        marginTop: 54,
        minHeight: `${window.innerHeight * (socialFooter ? 0.8 : 1.0)}px`,
      },
      contentShift: {
        [theme.breakpoints.up("md")]: {
          transition: theme.transitions.create("margin", {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginRight: "32%",
        },
      },
      form: {
        minHeight: "inherit",
      },
      boxRoot: {
        minHeight: "inherit",
      },
      customerText: {
        "&.clickable": {
          textDecoration: "underline 1pt",
          color: "#7c7cbf",
          cursor: "pointer",
        },
        "&.big": {
          fontSize: isMobile ? "0.75rem" : "",
        },
        "&.small": {
          fontSize: isMobile ? "0.6rem" : "",
        },
      },
    })
  );
  const classes = useStyles();

  useEffect(() => {
    const t = setTimeout(() => {
      resetCriteria();
      const elem = document.getElementsByTagName("html");
      if (elem && elem[0]) {
        elem[0].scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }, 500);

    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!cleverlynk?.takeOut && (!!!cleverlynk?.deliveryPrice || !cleverlynk?.geolocationInfo)) return;
    if (!deliveryType || !deliveryType) {
      if (cleverlynk?.takeOut && (!!cleverlynk?.deliveryPrice || cleverlynk?.geolocationInfo)) {
        handleModifyDeliveryType(PFXOrderFulfillmentType.DELIVERY);
      } else if (cleverlynk?.takeOut) {
        handleModifyDeliveryType(PFXOrderFulfillmentType.TAKEOUT);
      } else {
        handleModifyDeliveryType(PFXOrderFulfillmentType.DELIVERY);
      }
    }
  }, [cleverlynk]);

  useEffect(() => {
    if (ALIMENTEC_LIST.includes(cleverlynk?.id ?? "")) setOpenAuthDialog(false);
  }, [cleverlynk]);

  useEffect(() => {
    const appendableOptionalCriteria: PFXCriteriaForOrder[] = [];
    const appendableMandatoryCriteria: PFXCriteriaForOrder[] = [];
    if (customerInfo && cleverlynk) {
      cleverlynk.criteria?.forEach(cc => {
        if (cc.mandatory && customerInfo.mandatory) {
          const mc = customerInfo?.mandatory[cc.label!];
          if (mc) {
            if (mc.criteriaType === PFXCriteriaType.CALENDAR) {
              setCalendarInput(p => {
                const m = new Map(Array.from(p));
                m.set(mc.label!, moment(mc.value, DATE_FORMAT));
                return m;
              });
              appendableMandatoryCriteria.push(mc);
            } else if (mc.criteriaType === PFXCriteriaType.DROPDOWN) {
              if (cc.values?.includes(mc.value)) {
                appendableMandatoryCriteria.push(mc);
              }
            } else {
              appendableMandatoryCriteria.push(mc);
            }
          }
        } else if (!cc.mandatory && customerInfo.optional) {
          const oc = customerInfo?.optional[cc.label!];
          if (oc) {
            if (oc.criteriaType === PFXCriteriaType.CALENDAR) {
              setCalendarInput(p => {
                const m = new Map(Array.from(p));
                m.set(oc.label!, moment(oc.value, DATE_FORMAT));
                return m;
              });
              appendableOptionalCriteria.push(oc);
            } else if (oc.criteriaType === PFXCriteriaType.DROPDOWN) {
              if (cc.values?.includes(oc.value)) {
                appendableOptionalCriteria.push(oc);
              }
            } else {
              appendableOptionalCriteria.push(oc);
            }
          }
        }
      });
      handleModifyManyMandatoryCriteria(appendableMandatoryCriteria);
      handleModifyManyOptionalCriteria(appendableOptionalCriteria);
    }
  }, [customerInfo, cleverlynk]);

  useEffect(() => {
    setCalendarInput(prev => {
      const m = new Map(prev.entries());
      [...mandatoryCriteria, ...optionalCriteria]
        .filter(c => c.criteriaType === PFXCriteriaType.CALENDAR)
        .forEach(ele => m.set(ele?.label ?? "", moment(ele.value, DATE_FORMAT)));
      return m;
    });
  }, [cleverlynk]);

  const renderSelectInput = (label: string, mandatory: boolean, values: string[], type: string) => (
    <FormControl variant="outlined" fullWidth>
      <InputLabel id={label.toLowerCase().replaceAll(" ", "-")}>{label}</InputLabel>
      <Select
        SelectDisplayProps={{ style: { whiteSpace: "normal" } }}
        label={label}
        required={mandatory}
        id={onReplaceSpecialChars(label.toLowerCase().replaceAll(" ", "-"))}
        labelId={label.toLowerCase().replaceAll(" ", "-")}
        defaultValue={getDefaultValue(label, mandatory)}
        onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
          onInputChange({ label, value: event.target.value as string, type, mandatory })
        }>
        {values.map((value: string, index: number) => (
          <MenuItem key={index} value={value}>
            {value}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const renderCleverlynkForm = () => {
    if (cleverlynk && cleverlynk?.criteria) {
      return cleverlynk?.criteria.map((criteria: PFXCriteria, index: number) => {
        return (
          <Collapse
            style={{ width: "100%" }}
            in={
              !criteria.deliveryTypeExclusiveness ||
              PFXOrderFulfillmentType[deliveryType ?? ""] === criteria.deliveryTypeExclusiveness
            }>
            <Grid item xs={12} key={`criteria-${index}`} style={{ padding: 8 }}>
              {criteria.criteriaType === "TEXTFIELD" && (
                <UserFormTextField
                  // value={getDefaultValue(criteria.label!, criteria.mandatory!)}
                  value={getValue(criteria.label!, criteria.mandatory!)}
                  label={criteria.label!}
                  mandatory={
                    criteria.mandatory! &&
                    (criteria.deliveryTypeExclusiveness === PFXOrderFulfillmentType[deliveryType ?? ""] ||
                      !criteria.deliveryTypeExclusiveness)
                  }
                  characterLimit={criteria.characterLimit}
                  onInputChange={v =>
                    onInputChange({
                      label: criteria.label!,
                      value: v,
                      type: criteria.criteriaType!,
                      mandatory: criteria.mandatory!,
                    })
                  }
                />
              )}
              {criteria.criteriaType === "PHONE_NUMBER" && (
                <UserFormPhoneField
                  value={getValue(criteria.label!, criteria.mandatory!)}
                  label={criteria.label!}
                  mandatory={
                    criteria.mandatory! &&
                    (criteria.deliveryTypeExclusiveness === PFXOrderFulfillmentType[deliveryType ?? ""] ||
                      !criteria.deliveryTypeExclusiveness)
                  }
                  characterLimit={criteria.characterLimit}
                  onInputChange={v =>
                    onInputChange({
                      label: criteria.label!,
                      value: v,
                      type: criteria.criteriaType!,
                      mandatory: criteria.mandatory!,
                    })
                  }
                  classes={classes}
                />
              )}
              {criteria.criteriaType === "DROPDOWN" && criteria.values && (
                <UserFormSelectField
                  label={criteria.label!}
                  mandatory={
                    criteria.mandatory! &&
                    (criteria.deliveryTypeExclusiveness === PFXOrderFulfillmentType[deliveryType ?? ""] ||
                      !criteria.deliveryTypeExclusiveness)
                  }
                  value={getValue(criteria.label!, criteria.mandatory!)}
                  values={criteria.values}
                  onInputChange={v =>
                    onInputChange({
                      label: criteria.label!,
                      value: v,
                      type: criteria.criteriaType!,
                      mandatory: criteria.mandatory!,
                    })
                  }
                />
              )}
              {criteria.criteriaType === PFXCriteriaType.CALENDAR && (
                <UserFormDateField
                  value={calendarInput.get(criteria.label!)}
                  label={criteria.label!}
                  mandatory={
                    criteria.mandatory! &&
                    (criteria.deliveryTypeExclusiveness === PFXOrderFulfillmentType[deliveryType ?? ""] ||
                      !criteria.deliveryTypeExclusiveness)
                  }
                  onInputChange={date => {
                    onInputChange({
                      label: criteria.label!,
                      value: date?.format(DATE_FORMAT) ?? "",
                      type: PFXCriteriaType.CALENDAR,

                      mandatory: criteria.mandatory!,
                    });
                    setCalendarInput(m => {
                      const cop = new Map(m.entries());
                      cop.set(criteria.label!, date as moment.Moment);
                      return cop;
                    });
                  }}
                />
              )}
            </Grid>
          </Collapse>
        );
      });
    }
  };

  const renderTakeOutSelector = () => {
    const takeOut: PFXTakeOut = cleverlynk?.takeOut!;
    return renderSelectInput(
      takeOut.label!,
      deliveryType === PFXOrderFulfillmentType.TAKEOUT,
      takeOut.values!,
      takeOut.criteriaType!
    );
  };

  if (cleverlynkContext?.loading) {
    return <Fallback />;
  }

  return (
    <React.Fragment>
      <Header />
      <main
        className={clsx(classes.content, {
          [classes.contentShift]: cartIsOpen,
        })}>
        <Container className={classes.container}>
          <form
            className={classes.form}
            autoComplete="new-password"
            onSubmit={ev => {
              const propertyInfo = (document.getElementById("property_type_info_input") as HTMLInputElement)?.value;
              const additionalInfo = (document.getElementById("additional_info_input") as HTMLInputElement)?.value;
              ev.preventDefault();
              goToCheckout();
              handleAddCookie({ propertyInfo: propertyInfo ?? "", additionalInfo: additionalInfo ?? "" });
              if (customerInfo === undefined || !!customerInfo.consent) {
                handleUploadCustomerData(customerInfo ?? { mandatory: {}, optional: {}, consent: true });
              }
            }}>
            <ThemeProvider theme={createUserFormTheme(titleStyles?.textFontFamily ?? "Jost", primaryColor)}>
              <Box pb={9} className={classes.boxRoot}>
                <Grid container spacing={2} className={classes.slideUpwardsGrid}>
                  <Grid item>
                    <Typography variant="h4" style={{ fontWeight: 800, fontSize: isMobile ? "2.2rem" : "2.5rem" }}>
                      Ingresa tus datos
                    </Typography>
                    <Typography variant="caption" style={{ paddingTop: 12 }}>
                      Necesitamos algunos datos tuyos para continuar con la compra
                    </Typography>
                  </Grid>
                  {(!!cleverlynk?.deliveryPrice || cleverlynk?.geolocationInfo) && cleverlynk?.takeOut && (
                    <Collapse in={!clickedDeliverySlider} style={{ width: "100%" }}>
                      <Grid item xs={12}>
                        <Typography align="center" variant="h5" style={{ fontSize: "1.1rem", fontWeight: "bold" }}>
                          ¿Como quieres recibir tu pedido?
                        </Typography>
                      </Grid>
                    </Collapse>
                  )}
                  <DeliveryTypeSlider
                    onClick={() => {
                      if (!clickedDeliverySlider) {
                        setClickedDeliverySlider(true);
                      }
                    }}
                  />
                  <IdentificationField />
                  {(cleverlynk?.criteria ?? []).length > 0 && (
                    <Grid item xs={12}>
                      <div style={{ borderRadius: 10, backgroundColor: "white" }}>
                        <Box p={2}>
                          <Grid container spacing={2}>
                            {/* <Grid item xs={12}>
                              <Typography variant="subtitle1" color="primary">
                                <strong>{t("common:userFormInformation")}</strong>
                              </Typography>
                            </Grid> */}
                            {renderCleverlynkForm()}
                            <Collapse
                              in={cleverlynk?.takeOut && deliveryType === PFXOrderFulfillmentType.TAKEOUT}
                              style={{ width: "100%" }}>
                              <Grid item xs={12}>
                                {cleverlynk?.takeOut && <Box p={1}>{renderTakeOutSelector()}</Box>}
                              </Grid>
                            </Collapse>
                          </Grid>
                        </Box>
                      </div>
                    </Grid>
                  )}
                  {cleverlynk?.isUsingGeolocation && deliveryType === PFXOrderFulfillmentType.DELIVERY && (
                    <Grid item xs={12}>
                      <div style={{ borderRadius: 10, backgroundColor: "white" }}>
                        <Box p={1}>
                          <DeliveryInput />
                        </Box>
                      </div>
                    </Grid>
                  )}
                  {!cleverlynk?.isUsingGeolocation &&
                    !!cleverlynk?.geolocationInfo &&
                    deliveryType === PFXOrderFulfillmentType.DELIVERY && (
                      <Grid item xs={12}>
                        <div style={{ borderRadius: 10, backgroundColor: "white" }}>
                          <Box p={1}>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <Typography variant="subtitle2" color="primary">
                                  <strong>{t("common:deliveryInformation")}</strong>
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  autoComplete="new-password"
                                  label={t("common:address")}
                                  variant="outlined"
                                  required
                                  fullWidth
                                  value={deliveryInfo?.address ?? ""}
                                  onChange={ev =>
                                    handleModifyDeliveryInfo({
                                      address: ev.target.value,
                                    })
                                  }
                                />
                              </Grid>
                              <Grid item xs={12}>
                                <TextField
                                  autoComplete="new-password"
                                  required
                                  fullWidth
                                  variant="outlined"
                                  value={deliveryInfo.propertyInfo ?? ""}
                                  label={t("common:propertyType")}
                                  onChange={ev =>
                                    handleModifyDeliveryInfo({
                                      propertyInfo: ev.target.value,
                                    })
                                  }
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        </div>
                      </Grid>
                    )}
                  <Grid item xs={12} style={{ marginBottom: 50 }}>
                    <div style={{ borderRadius: 10, backgroundColor: "white" }}>
                      <Box p={2}>
                        <Grid container>
                          <Grid item xs={12} style={{ paddingBottom: 6 }}>
                            <Typography variant="subtitle2" color="primary">
                              <strong>{t("datatreatment:saveCustomerData.saveYourData")}</strong>
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  style={{ marginLeft: isMobile ? 0 : 10 }}
                                  checked={customerInfo === undefined || customerInfo.consent}
                                  disabled={!!customerInfo?.consent}
                                  onChange={e => toggleCustomerInfoConsent(e.target.checked)}
                                />
                              }
                              labelPlacement="end"
                              label={
                                <Grid container style={{ marginLeft: isMobile ? 0 : 10 }}>
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" className={`big ${classes.customerText}`}>
                                      {customerInfo === undefined || customerInfo.consent ? (
                                        <React.Fragment>
                                          {t("datatreatment:saveCustomerData.hyperlink1")}
                                          <Typography
                                            component="a"
                                            variant="subtitle2"
                                            className={`big clickable ${classes.customerText}`}
                                            onClick={() => {
                                              disableCustomerInfo();
                                            }}>
                                            {t("datatreatment:saveCustomerData.hyperlink2")}
                                          </Typography>
                                          {t("datatreatment:saveCustomerData.hyperlink3")}
                                        </React.Fragment>
                                      ) : (
                                        t("datatreatment:saveCustomerData.saveYourDataPrompr")
                                      )}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Typography variant="caption" className={`small ${classes.customerText}`}>
                                      {customerInfo === undefined || customerInfo.consent ? (
                                        <React.Fragment>
                                          {t("datatreatment:dataTreatment.dt1")}
                                          <Typography
                                            component="a"
                                            variant="caption"
                                            className={`small clickable ${classes.customerText}`}
                                            href={
                                              company?.termsURL ||
                                              "https://cleverlynk-imgs.s3.amazonaws.com/terms/Politicas+de+Privacidad.pdf"
                                            }
                                            target="__blank">
                                            {t("datatreatment:dataTreatment.dt2")}
                                          </Typography>
                                        </React.Fragment>
                                      ) : (
                                        <React.Fragment>
                                          {t("datatreatment:dataTreatment.dt3")}
                                          <Typography
                                            component="a"
                                            variant="caption"
                                            className={`small clickable ${classes.customerText}`}
                                            href={"https://cleverlynk-imgs.s3.amazonaws.com/terms/ADP_CLYNK.pdf"}
                                            target="__blank">
                                            {t("datatreatment:dataTreatment.dt4")}
                                          </Typography>
                                          {t("datatreatment:dataTreatment.dt5")}
                                          <Typography
                                            component="a"
                                            variant="caption"
                                            className={`small clickable ${classes.customerText}`}
                                            href={
                                              "https://cleverlynk-imgs.s3.amazonaws.com/terms/Politicas+de+Privacidad.pdf"
                                            }
                                            target="__blank">
                                            {t("datatreatment:dataTreatment.dt6")}
                                          </Typography>
                                          {t("datatreatment:dataTreatment.dt7")}
                                        </React.Fragment>
                                      )}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              }
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </div>
                  </Grid>
                </Grid>
              </Box>
            </ThemeProvider>
            <Box p={2} className={classes.stickToBottom}>
              <Button
                fullWidth
                disabled={
                  cleverlynk?.isUsingGeolocation &&
                  !selectedAddress &&
                  !(deliveryType === PFXOrderFulfillmentType.TAKEOUT)
                }
                size="large"
                className={classes.nextbutton}
                type="submit"
                color={
                  cleverlynk?.isUsingGeolocation &&
                  !selectedAddress &&
                  !(deliveryType === PFXOrderFulfillmentType.TAKEOUT)
                    ? "default"
                    : "primary"
                }
                id="next"
                variant="contained">
                {cleverlynk?.isUsingGeolocation &&
                !selectedAddress &&
                !(deliveryType === PFXOrderFulfillmentType.TAKEOUT)
                  ? t("common:chooseAnAddress")
                  : t("common:next")}
              </Button>
            </Box>
          </form>
        </Container>
        <AuthScreen
          open={openAuthDialog}
          setClose={() => {
            handleSetOpenedAuth();
            setOpenAuthDialog(false);
          }}
        />
        {!Boolean(zendesk) && <SocialSpeedDials />}
        <ClynkFooter />
        {/* <UpsellingDialog /> */}
      </main>
    </React.Fragment>
  );
};

export default UserForm;
